/**
 * Shared domain types — single source of truth across web + worker.
 * Keep these aligned with the Supabase schema (db/migrations).
 */

// ── Identity & tenancy ───────────────────────────────────────────────
export type Role = 'owner' | 'admin' | 'marketer' | 'viewer';

export const ROLES: readonly Role[] = ['owner', 'admin', 'marketer', 'viewer'] as const;

/** Role precedence, highest first. Used for role-gating checks. */
export const ROLE_RANK: Record<Role, number> = {
  owner: 3,
  admin: 2,
  marketer: 1,
  viewer: 0,
};

export type OrgStatus = 'active' | 'suspended';

export interface Org {
  id: string;
  name: string;
  slug: string;
  status: OrgStatus;
  default_currency: string;
  timezone: string;
  created_at: string;
  updated_at: string;
}

export interface Profile {
  user_id: string;
  display_name: string | null;
  avatar_url: string | null;
  created_at: string;
  updated_at: string;
}

export interface OrgMember {
  org_id: string;
  user_id: string;
  role: Role;
  invited_by: string | null;
  accepted_at: string | null;
  created_at: string;
}

/** Resolved auth context the Worker derives from a Supabase JWT. */
export interface AuthContext {
  userId: string;
  orgId: string;
  role: Role;
}

// ── Tenant site (each brand's own domain) ────────────────────────────
// The engine is tenant-agnostic, so nothing may default to one tenant's URL.
// Keyed by org slug — same pattern as the paradigm registry (creativeParadigm.ts).
// Used for the launch destination prefill and the ad-preview domain strip.
export const TENANT_SITES: Record<string, string> = {
  'phuket-cleanse': 'https://phuketcleanse.com/',
  propty: 'https://propty.uk/',
};

/**
 * The tenant's own site, or null when we don't know it. A null MUST be handled by
 * asking for a URL — never by falling back to another tenant's domain, which is
 * how a Phuket Cleanse link ended up as the default on every org's Launch page.
 */
export function tenantSite(slug: string | null | undefined): string | null {
  return TENANT_SITES[slug ?? ''] ?? null;
}

/**
 * Currency symbols, keyed by ISO code. Same intent as TENANT_SITES: a tenant's
 * money must never be rendered in another tenant's units.
 *
 * There is NO default symbol on purpose. Launch and Results used to hardcode '฿'
 * everywhere, so Propty — whose ad account bills in GBP on its own card — showed
 * its budget, spend and revenue in baht. Falling back to a house currency is what
 * produced that, so an unknown code falls back to the CODE itself ("PLN 500"),
 * which is unambiguous rather than quietly wrong.
 */
export const CURRENCY_SYMBOLS: Record<string, string> = {
  THB: '฿',
  USD: '$',
  EUR: '€',
  GBP: '£',
  AUD: 'A$',
  SGD: 'S$',
};

/** Symbol for an ISO code, else the code itself. Never another tenant's symbol. */
export function currencySymbol(code: string | null | undefined): string {
  const c = (code ?? '').toUpperCase();
  return CURRENCY_SYMBOLS[c] ?? (c || '');
}

/** Format an amount in the org's own currency, e.g. '£50' / 'PLN 500'. */
export function formatMoney(amount: number, code: string | null | undefined): string {
  const sym = currencySymbol(code);
  const n = amount.toLocaleString();
  // A bare ISO code needs a space; a glyph does not.
  return /^[A-Z]{3}$/.test(sym) ? `${sym} ${n}` : `${sym}${n}`;
}

/**
 * Daily-budget ceiling used ONLY when an org has no `budget_settings` row.
 * Deliberately low: a missing row is a misconfiguration, and the safe direction
 * is to make the user confirm an override rather than to inherit whatever number
 * the first tenant happened to pick. Seed a real per-org row instead of leaning
 * on this.
 */
export const FALLBACK_DAILY_CEILING = 50;

/** Bare display host for an ad preview, e.g. 'PROPTY.UK'. Null if unparseable. */
export function displayHost(url: string | null | undefined): string | null {
  if (!url) return null;
  try {
    return new URL(url).host.replace(/^www\./, '').toUpperCase();
  } catch {
    return null;
  }
}

// ── Un-retryable build failures ──────────────────────────────────────
/**
 * Opening words of a build error that retrying can never clear — Meta refusing
 * to promote a post (licensed music being the usual reason). The worker writes
 * it; the web reads it to hide a Retry button that is guaranteed to fail.
 */
export const UNBOOSTABLE_PREFIX = 'This post cannot be turned into an ad.';

/**
 * True when a stored build error is a Meta content rule rather than a fault.
 * Also matches the raw Meta subcodes so failures recorded BEFORE Basil started
 * translating them are treated correctly too.
 */
export function isUnretryableBuildError(message: string | null | undefined): boolean {
  if (!message) return false;
  return (
    message.startsWith(UNBOOSTABLE_PREFIX) ||
    /\b2875030\b|\b1487472\b|copyrighted music|can't be promoted in an ad/i.test(message)
  );
}

// ── API envelope ─────────────────────────────────────────────────────
export type ApiOk<T> = { ok: true; data: T };
export type ApiErr = { ok: false; error: { code: string; message: string } };
export type ApiResult<T> = ApiOk<T> | ApiErr;
